import { useState, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';

/**
 * Mesma checagem do domínio, com o mesmo propósito limitado: barrar erro de
 * digitação óbvio. Quem decide se um e-mail existe é a entrega, e quem recusa
 * em definitivo é o servidor.
 */
const FORMATO = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

export const MAXIMO_EMAILS = 20;

/**
 * Lista de e-mails do cliente: digitar, adicionar, ver e remover.
 *
 * O componente não fala com a API. A lista vive no formulário e sobe junto no
 * salvar, então fechar o diálogo no meio não deixa metade das alterações
 * gravadas.
 */
export function CampoEmails({
  emails,
  aoMudar,
}: {
  emails: readonly string[];
  aoMudar: (emails: string[]) => void;
}) {
  const [rascunho, setRascunho] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const normalizado = rascunho.trim().toLowerCase();
  const cheio = emails.length >= MAXIMO_EMAILS;

  function adicionar() {
    if (normalizado === '') return;

    if (!FORMATO.test(normalizado)) {
      setErro('E-mail inválido.');
      return;
    }
    if (emails.includes(normalizado)) {
      setErro('Este e-mail já está na lista.');
      return;
    }
    if (cheio) {
      setErro(`No máximo ${MAXIMO_EMAILS} e-mails.`);
      return;
    }

    aoMudar([...emails, normalizado]);
    setRascunho('');
    setErro(null);
  }

  function remover(email: string) {
    aoMudar(emails.filter((atual) => atual !== email));
    setErro(null);
  }

  // Enter adiciona sem enviar o formulário — o diálogo inteiro é um <form>, e
  // sem isto digitar um e-mail e apertar Enter salvaria o cliente.
  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    if (evento.key !== 'Enter') return;
    evento.preventDefault();
    adicionar();
  }

  return (
    <Box>
      <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>
        E-mails
      </FormLabel>

      <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          type="email"
          placeholder="contato@empresa.com.br"
          value={rascunho}
          onChange={(evento) => {
            setRascunho(evento.target.value);
            if (erro !== null) setErro(null);
          }}
          onKeyDown={aoTeclar}
          error={erro !== null}
          helperText={erro ?? ' '}
          disabled={cheio}
          slotProps={{ htmlInput: { maxLength: 254, 'aria-label': 'E-mail' } }}
        />

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={adicionar}
          disabled={normalizado === '' || cheio}
          sx={{ mt: 0.25, flexShrink: 0 }}
        >
          Adicionar
        </Button>
      </Stack>

      {emails.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Nenhum e-mail cadastrado.
        </Typography>
      ) : (
        <List
          dense
          disablePadding
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 0.5 }}
        >
          {emails.map((email, indice) => (
            <ListItem
              key={email}
              divider={indice < emails.length - 1}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => remover(email)}
                  aria-label={`Remover ${email}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={email}
                slotProps={{ primary: { sx: { fontSize: '0.875rem', wordBreak: 'break-all' } } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
